import { Box, Flex, Text } from "@chakra-ui/react";
import type { ReactElement } from "react";
import type { PublicDestinationTourRow } from "@otalytics/shared";

interface LeaderboardTableProps {
  rows: PublicDestinationTourRow[];
  /**
   * `"compact"` renders only rank, title and price — a structural drop, not a
   * viewport-keyed hide. The movers grid sits two tables wide in a 1040px
   * container, so each table's track is ~468px; the operator/rating/reviews
   * cells claim ~200px of fixed width and starve the title into slivers there.
   */
  variant?: "full" | "compact";
}

/**
 * Price, rating and review columns are frequently null across a whole SERP —
 * the scraper only records them when the card actually exposed them. Each
 * column is therefore dropped entirely when no row has it, rather than
 * rendering a column of dashes.
 */
export function LeaderboardTable({
  rows,
  variant = "full",
}: LeaderboardTableProps): ReactElement {
  const isCompact = variant === "compact";
  const hasPrice = rows.some((row) => row.priceEur != null);
  const hasRating = !isCompact && rows.some((row) => row.rating != null);
  const hasReviews = !isCompact && rows.some((row) => row.reviewCount != null);

  /* A column earns its width when most rows fill it. Operator attribution is
     thin on plenty of SERPs — Rome carries 8 of 17 — and a column left blank
     on half its rows reads as broken rather than as unknown, opening a dead
     vertical band down the middle of the table. Below two thirds the name
     moves inline under the tour title, where a missing value costs nothing
     and the width goes back to the titles, which were truncating. */
  const operatorRowCount = rows.filter((row) => row.operatorName != null).length;
  const hasAnyOperator = !isCompact && operatorRowCount > 0;
  const hasOperatorColumn =
    hasAnyOperator && operatorRowCount * 3 >= rows.length * 2;
  const hasInlineOperator = hasAnyOperator && !hasOperatorColumn;

  /* The rank column legitimately skips numbers — this page ranks 17 tours
     across positions 1–20 — and `buildLeaderboard` filters nothing, so every
     row the scrape recorded is here. The caption that says so lives with the
     section heading, not under the table.

     Seventeen rows at one pitch is a wall with no resting point. The page's
     own language already splits at ten ("the top ten, against the page"), so
     the pause lands on the first row below it rather than at an arbitrary
     midpoint — and only when the table is long enough to need one. */
  const secondHalfIndex = isCompact
    ? -1
    : rows.findIndex((row) => row.position > 10);
  const chunkIndex =
    secondHalfIndex > 0 && rows.length > 12 ? secondHalfIndex : -1;

  return (
    <Box>
      <Flex
        alignItems="baseline"
        gap={{ base: 3, md: 4 }}
        pb={2}
        borderBottom="1px solid var(--color-border)"
      >
        {/* 34px, not 26: "RANK" at 10px/600 with 0.06em tracking needs ~32px
            and was breaking to a second line as "RAN / K". */}
        <ColumnLabel w="34px">Rank</ColumnLabel>
        <Box flex="1 1 0" minW={0}>
          <ColumnLabel>Tour</ColumnLabel>
        </Box>
        {hasOperatorColumn && (
          <ColumnLabel minW="96px" hideBelow>
            Operator
          </ColumnLabel>
        )}
        {hasRating && (
          <ColumnLabel minW="44px" align="right" hideBelow>
            Rating
          </ColumnLabel>
        )}
        {hasReviews && (
          <ColumnLabel minW="64px" align="right" hideBelow>
            Reviews
          </ColumnLabel>
        )}
        {hasPrice && (
          <ColumnLabel minW="52px" align="right">
            From
          </ColumnLabel>
        )}
      </Flex>

      {rows.map((row, index) => (
        <Flex
          key={`${row.position}-${row.url ?? row.title ?? ""}`}
          alignItems="baseline"
          gap={{ base: 3, md: 4 }}
          pb={3}
          pt={index === chunkIndex ? { base: 6, md: 7 } : 3}
          borderTop={
            index === chunkIndex ? "1px solid var(--color-border)" : undefined
          }
          borderBottom="1px solid var(--color-border-subtle)"
        >
          {/* Rank is the subject of this page — "Who ranks in Rome" — and was
              rendering in the same tertiary grey as operator, rating and review
              count, which made it the faintest cell on its own row. Primary ink
              at 15px makes the rank column the thing the eye tracks down. */}
          <Text
            className="tabular-nums"
            fontFamily="var(--font-geist-mono)"
            fontSize={{ base: "14px", md: "15px" }}
            fontWeight="500"
            color="var(--color-text-primary)"
            w="34px"
            flexShrink={0}
          >
            {row.position}
          </Text>

          <Box flex="1 1 0" minW={0}>
            {/* One line, always. A wrapped title pushes its row taller than
                its siblings and breaks the table's vertical rhythm — worst in
                the movers pair, where two tables share the width. `title`
                carries the full text for anyone who needs it. */}
            {row.url ? (
              <a
                href={row.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                title={row.title ?? undefined}
                style={{ display: "block", minWidth: 0 }}
              >
                <Text
                  fontSize={{ base: "14px", md: "15px" }}
                  color="var(--color-text-primary)"
                  lineHeight="1.45"
                  transition="var(--transition-fast)"
                  _hover={{ color: "var(--color-primary)" }}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  minW={0}
                >
                  {row.title ?? "Untitled tour"}
                </Text>
              </a>
            ) : (
              <Text
                fontSize={{ base: "14px", md: "15px" }}
                color="var(--color-text-primary)"
                lineHeight="1.45"
                title={row.title ?? undefined}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                minW={0}
              >
                {row.title ?? "Untitled tour"}
              </Text>
            )}

            {/* The supplier sits with the title when it is too sparse to be a
                column, sharing the meta line with the movement chip. */}
            <Flex alignItems="baseline" gap={2} flexWrap="wrap" minW={0}>
              {hasInlineOperator && row.operatorName != null && (
                <Text
                  fontSize="11px"
                  color="var(--color-text-tertiary)"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxW="100%"
                  mt={0.5}
                >
                  {row.operatorName}
                </Text>
              )}
              <Movement row={row} />
            </Flex>
          </Box>

          {hasOperatorColumn && (
            <Text
              fontSize="12px"
              color="var(--color-text-secondary)"
              flexShrink={0}
              minW="96px"
              maxW="140px"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              display={{ base: "none", sm: "block" }}
            >
              {row.operatorName ?? ""}
            </Text>
          )}

          {/* A recorded value outranks an absent one: the figure reads at
              secondary, the em dash stays tertiary. They used to share a
              colour, so a dash carried the same weight as a rating. */}
          {hasRating && (
            <Text
              className="tabular-nums"
              fontFamily="var(--font-geist-mono)"
              fontSize="12px"
              color={
                row.rating != null
                  ? "var(--color-text-secondary)"
                  : "var(--color-text-tertiary)"
              }
              flexShrink={0}
              minW="44px"
              textAlign="right"
              display={{ base: "none", sm: "block" }}
            >
              {row.rating != null ? row.rating.toFixed(1) : "—"}
            </Text>
          )}

          {hasReviews && (
            <Text
              className="tabular-nums"
              fontFamily="var(--font-geist-mono)"
              fontSize="12px"
              color={
                row.reviewCount != null
                  ? "var(--color-text-secondary)"
                  : "var(--color-text-tertiary)"
              }
              flexShrink={0}
              minW="64px"
              textAlign="right"
              display={{ base: "none", sm: "block" }}
            >
              {row.reviewCount != null
                ? row.reviewCount.toLocaleString("en-GB")
                : "—"}
            </Text>
          )}

          {hasPrice && (
            <Text
              className="tabular-nums"
              fontSize={{ base: "13px", md: "14px" }}
              fontWeight="600"
              color="var(--color-text-primary)"
              flexShrink={0}
              minW="52px"
              textAlign="right"
            >
              {row.priceEur != null ? `€${Math.round(row.priceEur)}` : "—"}
            </Text>
          )}
        </Flex>
      ))}
    </Box>
  );
}

/** Only rendered when the tour was actually in the earlier SERP. */
function Movement({ row }: { row: PublicDestinationTourRow }): ReactElement | null {
  if (row.movementAvailable === false) {
    return null;
  }
  if (row.previousPosition == null) {
    return (
      <Text
        fontFamily="var(--font-geist-mono)"
        fontSize="11px"
        color="var(--color-text-tertiary)"
        mt={0.5}
      >
        new this month
      </Text>
    );
  }

  const delta = row.previousPosition - row.position;
  if (delta === 0) {
    return null;
  }

  return (
    <Text
      className="tabular-nums"
      fontFamily="var(--font-geist-mono)"
      fontSize="11px"
      color={delta > 0 ? "var(--color-success-strong)" : "var(--color-error-strong)"}
      mt={0.5}
    >
      {delta > 0 ? `▲ ${delta}` : `▼ ${Math.abs(delta)}`} from{" "}
      {row.previousPosition}
    </Text>
  );
}

/**
 * A column heading. Kept deliberately quiet — it exists to name the number
 * below it, not to compete with the data for attention.
 *
 * `hideBelow` mirrors the cell it labels: rating and reviews are hidden on the
 * narrowest screens, and a heading with no column under it is worse than none.
 */
function ColumnLabel({
  children,
  w,
  minW,
  align = "left",
  hideBelow = false,
}: {
  children: string;
  w?: string;
  minW?: string;
  align?: "left" | "right";
  hideBelow?: boolean;
}): ReactElement {
  return (
    <Text
      fontSize="11px"
      fontWeight="600"
      letterSpacing="0.06em"
      textTransform="uppercase"
      color="var(--color-text-tertiary)"
      whiteSpace="nowrap"
      w={w}
      minW={minW}
      flexShrink={0}
      textAlign={align}
      display={hideBelow ? { base: "none", sm: "block" } : undefined}
    >
      {children}
    </Text>
  );
}
